/**
 * A foto de satélite por baixo dos dados, a partir do zoom máximo do GOES.
 *
 * O GOES-19 tem ~1 km por pixel: no zoom máximo dele (`GOES_MAX_ZOOM`, ~220 m
 * por pixel da tela) a imagem já é um borrão. Dali para dentro entra a foto de
 * satélite de alta resolução, com as ruas e os nomes por cima, por baixo da
 * imagem do GOES e de tudo que é dado — radar, limites, rios, estações, focos —
 * para mostrar o que há no chão.
 *
 * Quem fornece (fundoCreditos.ts): o **Esri World Imagery** com a chave da
 * ArcGIS Location Platform (docs/fundo-esri.md) ou, sem a chave, o
 * **EOxCloudless** do Sentinel-2 (docs/fundo-satelite.md). Até 04/11/2026 a
 * foto era a do Google, pedida fora da API oficial, sem cobertura dos termos.
 *
 * - Sem botão: a camada é do mapa, não do usuário. O OpenLayers só pede blocos
 *   de camadas dentro da faixa de resolução (`maxResolution`), então NADA da
 *   foto é baixado nem reprojetado antes do zoom máximo do GOES.
 * - Blocos em Web Mercator, com CORS (a exportação continua lendo o canvas),
 *   reprojetados para a grade do GOES — barato nessa escala, em que a projeção
 *   é praticamente plana.
 * - Transição: a partir do zoom máximo do GOES a imagem do satélite vai
 *   ficando transparente (`goesFade`) e a foto aparece por baixo — nuvens
 *   sobre o chão no meio do caminho —, até sumir `GOES_FADE_ZOOMS` níveis
 *   depois, quando deixa de ser desenhada (`goesMinResolution`).
 * - Se os blocos falharem em sequência (bloqueio, cota esgotada, sem rede), o
 *   GOES volta inteiro a todos os zooms: ampliado, mas nunca tela preta.
 */
import type ImageTile from 'ol/ImageTile';
import type OlMap from 'ol/Map';
import type Tile from 'ol/Tile';
import TileState from 'ol/TileState';
import TileLayer from 'ol/layer/Tile';
import XYZ from 'ol/source/XYZ';
import { create } from 'zustand';
import { GOES_MIN_RESOLUTION } from './createMap';
import { PROVEDOR_FUNDO, type ProvedorFundo } from './fundoCreditos';
import { Z } from './zIndex';

export { EOX_ANO, FOTO_ATTRIBUTION, FOTO_CREDITO_TEXTO, PROVEDOR_FUNDO, ROTULOS_ATTRIBUTION, ROTULOS_CREDITO_TEXTO } from './fundoCreditos';

/** Último nível pedido da foto; dali para dentro o mapa amplia. */
export const FOTO_MAX_TILE_ZOOM = PROVEDOR_FUNDO.fotoMaxZoom;

/** Último nível dos rótulos. */
export const ROTULOS_MAX_TILE_ZOOM = PROVEDOR_FUNDO.rotulosMaxZoom;

/** Resolução em Web Mercator (m/px na tela, no equador) de um nível z de blocos de 256 px. */
const resolucaoWebMercator = (z: number) => 156543.03392804097 / 2 ** z;

/**
 * Até onde os rótulos aparecem (0 = sempre). Na EOX eles param no z14 e,
 * ampliados mais de ~1,5 vez, viram faixas de letras — no julgamento de
 * 04/11/2026, nomes de rua do tamanho de um quarteirão por cima dos nomes das
 * cidades. Os da Esri vão até o z22: sempre. A projeção do GOES vale ~1 m por
 * metro na Amazônia, então a resolução de Web Mercator serve direto.
 */
export function rotulosMinResolution(p: ProvedorFundo = PROVEDOR_FUNDO): number {
  if (!Number.isFinite(p.rotulosNiveisAmpliados)) return 0;
  return resolucaoWebMercator(p.rotulosMaxZoom + p.rotulosNiveisAmpliados);
}

/**
 * A foto aparece a partir daqui. A folga de 1% conta como "alcançou" o zoom
 * que para uma animação a um fio de cabelo do limite do GOES.
 */
export const FUNDO_MAX_RESOLUTION = GOES_MIN_RESOLUTION * 1.01;

/**
 * Níveis de zoom em que o GOES vai do opaco ao transparente depois do zoom
 * máximo dele: do 7 (~220 m por pixel) ao 10 (~27 m), onde um pixel do GOES já
 * cobre ~37 da tela.
 */
export const GOES_FADE_ZOOMS = 3;

/** Falhas (ou acertos) seguidos que devolvem o GOES inteiro (ou a foto). */
const SEQUENCIA_PARA_TROCAR = 4;

/** Quantos níveis acima, no máximo, se procura um bloco quando o pedido dá 404. */
export const NIVEIS_DE_RECUO = 4;

/** O endereço de um bloco a partir do modelo `{z}/{y}/{x}`. */
export function enderecoDoBloco(modelo: string, z: number, x: number, y: number): string {
  return modelo.replace('{z}', String(z)).replace('{y}', String(y)).replace('{x}', String(x));
}

/**
 * Onde, no bloco `d` níveis acima, fica o bloco (z, x, y): o bloco de cima e o
 * recorte dele (origem e lado, em frações de 0 a 1).
 */
export function recorteDoAncestral(z: number, x: number, y: number, d: number): { z: number; x: number; y: number; ox: number; oy: number; lado: number } {
  const n = 2 ** d;
  return { z: z - d, x: Math.floor(x / n), y: Math.floor(y / n), ox: (x % n) / n, oy: (y % n) / n, lado: 1 / n };
}

/**
 * Carrega o bloco da foto e, se ele não existir (404: a Esri não tem foto
 * naquele nível — na Amazônia rural, nada além do z17), recorta e amplia o
 * bloco de um nível acima, até `NIVEIS_DE_RECUO`. Sem isso o lugar ficava
 * vazio, e com 4 falhas seguidas o mapa voltava ao GOES, justamente onde
 * estão os focos. Os pedidos são `fetch` com CORS, e o resultado vira um blob
 * local: o canvas continua legível para a exportação.
 */
function carregarComRecuo(modelo: string) {
  /**
   * Blocos que existem mas não têm nada mais detalhado ("z/x/y"): achado o
   * primeiro, os vizinhos vão direto a ele, sem repetir os 404 (que a Esri
   * não deixa o navegador guardar). Na Amazônia rural, 1 pedido por bloco em
   * vez de 3 no zoom máximo.
   */
  const semFilhos = new Set<string>();
  return (tile: Tile, src: string) => {
    const img = (tile as ImageTile).getImage() as HTMLImageElement;
    const [z, x, y] = tile.getTileCoord();
    const mostrar = (blob: Blob) => {
      const url = URL.createObjectURL(blob);
      img.addEventListener('load', () => URL.revokeObjectURL(url), { once: true });
      img.addEventListener('error', () => URL.revokeObjectURL(url), { once: true });
      img.src = url;
    };
    const falhar = () => tile.setState(TileState.ERROR);
    void (async () => {
      try {
        // Um ancestral já conhecido como "sem nada mais detalhado"? Vai direto a ele.
        let inicio = 0;
        for (let d = 1; d <= NIVEIS_DE_RECUO && z - d >= 0 && !inicio; d++) {
          const a = recorteDoAncestral(z, x, y, d);
          if (semFilhos.has(`${a.z}/${a.x}/${a.y}`)) inicio = d;
        }
        if (!inicio) {
          const r = await fetch(src, { mode: 'cors', credentials: 'omit' });
          if (r.ok) return mostrar(await r.blob());
          if (r.status !== 404) return falhar();
          inicio = 1;
        }
        for (let d = inicio; d <= NIVEIS_DE_RECUO && z - d >= 0; d++) {
          const a = recorteDoAncestral(z, x, y, d);
          const ra = await fetch(enderecoDoBloco(modelo, a.z, a.x, a.y), { mode: 'cors', credentials: 'omit' });
          if (ra.status === 404) continue;
          if (!ra.ok) return falhar();
          semFilhos.add(`${a.z}/${a.x}/${a.y}`);
          const bmp = await createImageBitmap(await ra.blob());
          const tela = document.createElement('canvas');
          tela.width = bmp.width;
          tela.height = bmp.height;
          const ctx = tela.getContext('2d')!;
          ctx.imageSmoothingQuality = 'high';
          ctx.drawImage(bmp, a.ox * bmp.width, a.oy * bmp.height, a.lado * bmp.width, a.lado * bmp.height, 0, 0, tela.width, tela.height);
          bmp.close();
          const blob = await new Promise<Blob | null>((ok) => tela.toBlob(ok, 'image/jpeg', 0.9));
          return blob ? mostrar(blob) : falhar();
        }
        falhar();
      } catch {
        falhar();
      }
    })();
  };
}

/** A fonte da foto, a mesma no mapa principal, no fundo sem a NOAA e no mapa da denúncia. */
export function fotoSource(p: ProvedorFundo = PROVEDOR_FUNDO): XYZ {
  return new XYZ({
    url: p.fotoUrl,
    maxZoom: p.fotoMaxZoom,
    crossOrigin: 'anonymous',
    attributions: p.fotoAttribution,
    ...(p.recuaNo404 ? { tileLoadFunction: carregarComRecuo(p.fotoUrl) } : {}),
  });
}

/**
 * Os rótulos só entram onde a foto já aparece de verdade: o nível 8,5, com o
 * GOES pela metade. Antes disso, sob as nuvens, eles repetiam os nomes do
 * próprio mapa ("Manaus" duas vezes, julgamento de 04/11/2026). No "Próximos
 * dias" valem em todos os zooms (previsao/ocultar.ts), pois lá os nomes do
 * mapa saem.
 */
export const ROTULOS_MAX_RESOLUTION = GOES_MIN_RESOLUTION / 2 ** 1.5;

/** A fonte dos rótulos (ruas e nomes). */
export function rotulosSource(p: ProvedorFundo = PROVEDOR_FUNDO): XYZ {
  return new XYZ({
    url: p.rotulosUrl,
    tileSize: p.rotulosTileSize,
    maxZoom: p.rotulosMaxZoom,
    crossOrigin: 'anonymous',
    attributions: p.rotulosAttribution,
  });
}

interface FundoState {
  /** Os blocos estão falhando em sequência: o GOES fica inteiro em todos os zooms. */
  falhou: boolean;
}

export const useFundoStore = create<FundoState>()(() => ({ falhou: false }));

/**
 * Quanto da opacidade escolhida o GOES mostra nesta resolução: 1 até o zoom
 * máximo dele, caindo em linha reta (por nível de zoom) até 0 `GOES_FADE_ZOOMS`
 * níveis depois. Sempre 1 se a foto falhou.
 */
export function goesFade(resolution: number | undefined): number {
  if (useFundoStore.getState().falhou || !resolution) return 1;
  const niveis = Math.log2(GOES_MIN_RESOLUTION / resolution);
  return Math.min(1, Math.max(0, 1 - niveis / GOES_FADE_ZOOMS));
}

/** Menor resolução em que as camadas do GOES ainda são desenhadas (0 = sempre): o fim da transição. */
export function goesMinResolution(): number {
  return useFundoStore.getState().falhou ? 0 : GOES_MIN_RESOLUTION / 2 ** GOES_FADE_ZOOMS;
}

export class FundoLayerManager {
  /** A foto de satélite. */
  readonly layer: TileLayer<XYZ>;
  /** As ruas e os nomes, logo acima da foto (e abaixo do GOES). */
  readonly rotulos: TileLayer<XYZ>;
  private falhasSeguidas = 0;
  private acertosSeguidos = 0;

  constructor(private readonly map: OlMap) {
    const source = fotoSource();
    source.on('tileloadend', () => this.registrar(true));
    source.on('tileloaderror', () => this.registrar(false));
    this.layer = new TileLayer({
      source,
      maxResolution: FUNDO_MAX_RESOLUTION,
      zIndex: Z.FUNDO,
      preload: 0,
      className: 'ol-layer fundo-layer',
    });
    this.layer.set('title', `Foto de satélite (${PROVEDOR_FUNDO.nome})`);
    this.rotulos = new TileLayer({
      source: rotulosSource(),
      maxResolution: ROTULOS_MAX_RESOLUTION,
      minResolution: rotulosMinResolution(),
      zIndex: Z.FUNDO_ROTULOS,
      preload: 0,
      className: 'ol-layer fundo-rotulos-layer',
    });
    this.rotulos.set('title', 'Ruas e nomes');
    map.addLayer(this.layer);
    map.addLayer(this.rotulos);
  }

  private registrar(ok: boolean): void {
    this.falhasSeguidas = ok ? 0 : this.falhasSeguidas + 1;
    this.acertosSeguidos = ok ? this.acertosSeguidos + 1 : 0;
    const { falhou } = useFundoStore.getState();
    if (!falhou && this.falhasSeguidas >= SEQUENCIA_PARA_TROCAR) useFundoStore.setState({ falhou: true });
    if (falhou && this.acertosSeguidos >= SEQUENCIA_PARA_TROCAR) useFundoStore.setState({ falhou: false });
  }

  dispose(): void {
    for (const l of [this.layer, this.rotulos]) {
      this.map.removeLayer(l);
      l.getSource()?.dispose();
      l.dispose();
    }
    useFundoStore.setState({ falhou: false });
  }
}
